import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEnvValue } from '../../../../Helper/env';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../../Config/constants';
import { pluralize, smartJoin } from '../../../../Helper';
import CartHelper from '../../../../Helper/Cart';
import DateHelper from '../../../../Helper/Date';
import MealPlanHelper from '../../../../Helper/MealPlan';
import useAlert from '../../../../Hooks/useAlert';
import useMealPlan from '../../../../Hooks/useMealPlan';
import useMealPlanHint from '../../../../Hooks/useMealPlanHint';
import useSetCheckoutStoreInfo from '../../../../Hooks/useSetCheckoutStoreInfo';
import routeList, { RootRouteProps } from '../../../../Routes/list';
import {
  removeMealPlanDaysItems,
  setMealPlanCheckingOut,
  setMealPlanDay,
  setMealPlanHint,
} from '../../../../RTK/mealPlan';
import {
  selectMealPlanCart,
  selectMealPlanCartExceedMonday,
  selectMealPlanCartFlat,
} from '../../../../RTK/mealPlan/selectors';
import checkoutApi from '../../../../Service/api/checkout';

import modals from '../../modals';

import useMealPlanNavigation from './useMealPlanNavigation';

const CARD_HEIGHT = 96;
const MAX_HEIGHT = constants.DEVICE_HEIGHT * 0.45; // 45% of screen height, not included the breadcrumbs and navigation button
const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');

const useMealPlanList = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const { disableMealPlanHint } = useMealPlanHint();
  const {
    isMealPlanAndHasType,
    mealPlanDay,
    mealPlanDayCount,
    mealPlanDiscount,
    mealPlanDispatchTime,
    shopData,
    validateMealPlan,
  } = useMealPlan();
  const {
    days,
    isDayHasCart,
    isFirstDay,
    isLastDay,
    selectedDayCart,
    selectedDayIndex,
    onBreadCrumbsPress,
    onPrev,
    onNext,
  } = useMealPlanNavigation();
  const navigation = useNavigation<StackNavigationProp<RootRouteProps>>();
  const isMounted = useRef(false);
  const setCheckoutStoreInfo = useSetCheckoutStoreInfo();
  const allScheduleCart = useSelector(selectMealPlanCart);
  const allCartItems = useSelector(selectMealPlanCartFlat);
  const [isBrandedPaymentLinkLoading, setBrandedPaymentLinkLoading] =
    useState(false);
  const cartExceedMonday = useSelector((state) =>
    selectMealPlanCartExceedMonday(state, mealPlanDay?.value),
  );

  const tabRoutes = useMemo(() => {
    if (days.length === 0) return [];
    else {
      return days.map((e) => ({
        key: e.label,
        title: e.label,
        value: e.value,
      }));
    }
  }, [days]);
  const sceneHeight = useMemo(() => {
    const noteHeight = isFirstDay || cartExceedMonday.isExceed ? 46 : 0;
    if (constants.isWeb) return 0; // no need for web
    if (selectedDayCart.length === 0) return 240;
    const height = CARD_HEIGHT * selectedDayCart.length;
    return height >= MAX_HEIGHT ? MAX_HEIGHT : height + noteHeight;
  }, [cartExceedMonday.isExceed, isFirstDay, selectedDayCart.length]);

  // effect for:
  // - clearing items on tuesday and thursday
  // - changing selected day to previous day if selected is tuesday or thursday (when changed the plan from 5_day to 3_day)
  // on mobile only trigger once on mount since you can't change the plan while the list is open
  // on web should not trigger on mount and should gets trigger only when plan changed
  useEffect(() => {
    const shouldTrigger = constants.isWeb ? isMounted.current : true;
    if (shouldTrigger) {
      if (mealPlanDayCount === 3) {
        // when it's true it means, user is from 5 day meal plan changing to 3 day meal plan
        const cleanUpDay = [2, 4]; // tuesday or thursday
        const cleanUpDaysItems = lodashFilter(allScheduleCart, (c) =>
          cleanUpDay.includes(c.day),
        ); // get all cart for cleanUpDay
        if (cleanUpDay.includes(mealPlanDay.value)) {
          // currently selected day is cleanUpDay
          dispatch(
            setMealPlanDay(lodashFind(days, { value: mealPlanDay.value - 1 })),
          ); // change it to monday or wednesday depending on the selected clean up day
        }
        if (!lodashIsEmpty(cleanUpDaysItems)) {
          // has item on cleanUpDay, remove it
          dispatch(
            removeMealPlanDaysItems(
              cleanUpDaysItems.map((c) => c.day), // return [2] or [4] or [2,4]
            ),
          );
        }
      }
    }
  }, [allScheduleCart, days, mealPlanDay.value, mealPlanDayCount, dispatch]);

  // effect for only setting isMounted to true or false, needs to be on the lowest part of all effect
  useEffect(() => {
    // NOTE - showFloatingButtonHint do nothing on web
    isMounted.current = true;
    dispatch(setMealPlanHint({ showFloatingButtonHint: false })); // if this mounted, it means floating button hint should not show
    return () => {
      isMounted.current = false;
      dispatch(setMealPlanHint({ showFloatingButtonHint: true })); // if this unmount, it means floating button hint should show
    };
  }, [dispatch]);

  const _onCreate = async () => {
    // Validation 1: check current day cart if valid
    const isCartValid = validateMealPlan();
    if (!isCartValid) return; // stop the code from going if current day cart is not valid
    // Validation 2: check all cart if all
    const maxQty = CartHelper.getTotalItemsCount(
      lodashFind(allScheduleCart, { day: 1 }).items,
    );
    const invalidCart = allScheduleCart.filter(
      (cart) => maxQty !== CartHelper.getTotalItemsCount(cart.items),
    )[0];
    if (!lodashIsEmpty(invalidCart)) {
      const remaining =
        maxQty - CartHelper.getTotalItemsCount(invalidCart.items);
      // change active day
      onBreadCrumbsPress(
        MealPlanHelper.getDayValue(mealPlanDayCount, invalidCart.day),
      );
      // show prompt
      return alert(
        'Item Count Must Match Monday',
        `You need to add ${remaining} more ${pluralize(
          remaining,
          'item',
        )} on ${DateHelper.convertNumberToDay(
          invalidCart.day,
        )} to proceed creating your meal plan.`,
      );
    }
    // Validation 3: check all cart if has exclusive
    const exclusiveItems = lodashFilter(allCartItems, { is_exclusive: true });
    if (lodashIsEmpty(exclusiveItems)) {
      return alert(
        "You're Almost There!",
        `Don't forget to add an exclusive item on ${smartJoin(
          constants.MEAL_PLAN_EXCLUSIVE_AVAILABILITY.map(
            DateHelper.convertNumberToDay,
          ),
          ', ',
          ' or ',
        )} — it's required to finish setting up your meal plan.`,
      );
    }
    // Proceed redirecting to checkout page
    disableMealPlanHint(); // first meal plan checkout should disabled the hint next time
    if (constants.isWeb) {
      sessionStorage.removeItem(
        constants.MEAL_PLAN_CHECKOUT_VISITED_SESSION_KEY,
      ); // when user intent for checking out, remove the previous flag just in case it was not removed properly
    }
    if (RAZZLE_BUILD_MODE === 'branded') {
      // For branded revamp menu web
      setBrandedPaymentLinkLoading(true);
      const { ok, data } = await checkoutApi.placeOrderMealPlan(
        MealPlanHelper.getBasePayload({
          storeId: shopData.id,
          data: allScheduleCart,
        }),
      );
      if (ok) {
        window?.localStorage?.setItem('BRANDED_LVS', window?.location?.href);
        // checkout webview of branded
        navigation.navigate(routeList.CHECKOUT, {
          storeId: shopData.id,
          url: data?.redirect,
        });
      }
      setBrandedPaymentLinkLoading(false);
    } else {
      // For marketplace mobile & web
      // set checkout info base on shopData or storeInfo
      setCheckoutStoreInfo(shopData);
      dispatch(setMealPlanCheckingOut(true));
      await new Promise((r) => setTimeout(r, 0)); // defer, run next succeeding line on next tick so effect relying on above dispatch reflects first before navigating
      // close action sheet on mobile
      if (!constants.isWeb) {
        await modals.hide(modals.MEAL_PLAN_LIST);
      }
      // redirect to specified checkout page base on platform
      navigation.navigate(routeList.CHECKOUT);
    }
  };

  return {
    allCart: allCartItems,
    cartExceedMonday,
    days,
    dispatchTime: mealPlanDispatchTime,
    isBrandedPaymentLinkLoading,
    isDayHasCart,
    isFirstDay,
    isLastDay,
    isMealPlanAndHasType,
    mealPlanDiscount,
    sceneHeight,
    selectedDay: mealPlanDay,
    selectedDayIndex,
    tabRoutes,
    onBreadCrumbsPress,
    onCreate: _onCreate,
    onPrev,
    onNext,
  };
};

export default useMealPlanList;
